import type { ReactNode } from 'react'
import { useCallback } from 'react'

import { EmbeddedCheckoutDrawer } from '@/components/billing/embedded-checkout.organism'
import { useBillingClient } from '@/hooks/billing/use-billing-client.hook'
import { useEmbeddedCheckout } from '@/hooks/billing/use-embedded-checkout.hook'
import { usePaymentMethodCheckout } from '@/hooks/billing/use-payment-method-checkout.hook'
import { useSubscription } from '@/hooks/billing/use-subscription.hook'
import { useTopUpSplitView } from '@/hooks/billing/use-top-up-split-view.hook'

import type { UserInteractionContentData } from './user-interaction-content.interface'
import { UserInteractionContent } from './user-interaction-content.molecule'

const PRO_PLAN_NAME = 'Pro'

interface UserInteractionContentContainerProps {
  data: UserInteractionContentData
  className?: string
}

export const UserInteractionContentContainer: React.FC<UserInteractionContentContainerProps> = ({
  data,
  className,
}): ReactNode => {
  const { checkoutState, openCheckout, closeCheckout, handleCheckoutComplete } =
    useEmbeddedCheckout()
  const billingClient = useBillingClient()
  const { initiateCheckout, isLoading: isAddingCard } = usePaymentMethodCheckout(
    billingClient.setupPaymentMethodCheckout,
    (clientSecret) => openCheckout(clientSecret)
  )
  const { subscribe, isSubscribing } = useSubscription((clientSecret) => {
    openCheckout(clientSecret)
  })
  const topUpState = useTopUpSplitView()

  const handleUpgrade = useCallback(async (): Promise<void> => {
    await subscribe(PRO_PLAN_NAME, false)
  }, [subscribe])

  return (
    <>
      <UserInteractionContent
        data={data}
        onAddCard={initiateCheckout}
        isAddingCard={isAddingCard}
        onUpgrade={handleUpgrade}
        isUpgrading={isSubscribing}
        topUpState={topUpState}
        className={className}
      />
      <EmbeddedCheckoutDrawer
        isOpen={checkoutState.isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeCheckout()
        }}
        clientSecret={checkoutState.clientSecret}
        onComplete={handleCheckoutComplete}
      />
    </>
  )
}
